import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as validationRulesApi from '../api/validationRules';

export function ValidationConfig() {
  const { profileId } = useParams();
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [catalog, setCatalog] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [testData, setTestData] = useState('');

  const loadData = useCallback(async () => {
    if (!selectedOrg || !profileId || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [rules, profileConfigs] = await Promise.all([
        validationRulesApi.getValidationRulesCatalog(),
        validationRulesApi.getProfileValidationConfig(selectedOrg.id, profileId)
      ]);
      setCatalog(rules);
      setConfigs(profileConfigs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, profileId, canReadAssets]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleRule = (ruleId) => {
    const existing = configs.find(c => c.ruleId === ruleId);
    if (existing) {
      setConfigs(configs.map(c => 
        c.ruleId === ruleId ? { ...c, enabled: !c.enabled } : c
      ));
    } else {
      setConfigs([...configs, {
        ruleId,
        enabled: true,
        config: {},
        blockOnFail: true
      }]);
    }
  };

  const handleConfigChange = (ruleId, key, value) => {
    setConfigs(configs.map(c => 
      c.ruleId === ruleId ? { ...c, config: { ...c.config, [key]: value } } : c
    ));
  };

  const handleBlockOnFailChange = (ruleId, value) => {
    setConfigs(configs.map(c => 
      c.ruleId === ruleId ? { ...c, blockOnFail: value } : c
    ));
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    setError(null);
    try {
      const enabledConfigs = configs.filter(c => c.enabled);
      await validationRulesApi.setProfileValidationConfig(selectedOrg.id, profileId, enabledConfigs);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRunValidation = async () => {
    if (!selectedOrg || !testData) return;
    try {
      const data = JSON.parse(testData);
      const result = await validationRulesApi.runValidation(selectedOrg.id, profileId, data);
      setValidationResult(result);
    } catch (err) {
      setError(err.message);
    }
  };

  const isRuleEnabled = (ruleId) => {
    const config = configs.find(c => c.ruleId === ruleId);
    return config?.enabled || false;
  };

  const getRuleConfig = (ruleId) => {
    return configs.find(c => c.ruleId === ruleId) || { config: {}, blockOnFail: true };
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view validation config.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Validation Rules"
        subtitle={`Configure validation rules for profile ${profileId}`}
        breadcrumbs={[
          { label: 'Report Profiles', href: '/admin/report-profiles' },
          { label: 'Validation Rules' }
        ]}
      />

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading validation rules...</div>
      ) : (
        <div style={styles.content}>
          <div style={styles.rulesPanel}>
            <h3 style={styles.panelTitle}>Available Rules</h3>
            {catalog.map(rule => (
              <div key={rule.id} style={styles.ruleCard} data-testid={`rule-${rule.id}`}>
                <div style={styles.ruleHeader}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={isRuleEnabled(rule.id)}
                      onChange={() => handleToggleRule(rule.id)}
                      disabled={!isAdmin}
                      data-testid={`toggle-${rule.id}`}
                    />
                    <span style={styles.ruleName}>{rule.name}</span>
                  </label>
                  <span style={{
                    ...styles.severityBadge,
                    background: rule.severity === 'error' ? '#fed7d7' : '#fefcbf',
                    color: rule.severity === 'error' ? '#c53030' : '#975a16'
                  }}>
                    {rule.severity}
                  </span>
                </div>
                <p style={styles.ruleDescription}>{rule.description}</p>
                {isRuleEnabled(rule.id) && rule.configurable && (
                  <div style={styles.configSection}>
                    {Object.entries(rule.configSchema || {}).map(([key, schema]) => (
                      <div key={key} style={styles.configField}>
                        <label style={styles.configLabel}>{key}</label>
                        <input
                          type={schema.type === 'number' ? 'number' : 'text'}
                          value={getRuleConfig(rule.id).config[key] || ''}
                          onChange={(e) => handleConfigChange(rule.id, key, 
                            schema.type === 'number' ? parseFloat(e.target.value) : 
                            schema.type === 'array' ? e.target.value.split(',').map(s => s.trim()) :
                            e.target.value
                          )}
                          style={styles.configInput}
                          placeholder={schema.description}
                          disabled={!isAdmin}
                          data-testid={`config-${rule.id}-${key}`}
                        />
                      </div>
                    ))}
                    <div style={styles.configField}>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={getRuleConfig(rule.id).blockOnFail}
                          onChange={(e) => handleBlockOnFailChange(rule.id, e.target.checked)}
                          disabled={!isAdmin}
                          data-testid={`block-${rule.id}`}
                        />
                        Block download on fail
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {isAdmin && (
              <button
                onClick={handleSave}
                disabled={saving}
                style={styles.saveButton}
                data-testid="save-config-button"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            )}
          </div>

          <div style={styles.testPanel}>
            <h3 style={styles.panelTitle}>Test Validation</h3>
            <textarea
              value={testData}
              onChange={(e) => setTestData(e.target.value)}
              style={styles.testInput}
              placeholder='[{"siteId": "site-1", "energyDelivered": 100}]'
              rows={6}
              data-testid="test-data-input"
            />
            <button
              onClick={handleRunValidation}
              style={styles.testButton}
              data-testid="run-validation-button"
            >
              Run Validation
            </button>
            {validationResult && (
              <div style={styles.resultPanel} data-testid="validation-result">
                <div style={{
                  ...styles.resultHeader,
                  background: validationResult.passed ? '#c6f6d5' : '#fed7d7',
                  color: validationResult.passed ? '#276749' : '#c53030'
                }}>
                  {validationResult.passed ? 'PASSED' : `FAILED (${validationResult.blockers} blockers)`}
                </div>
                {validationResult.results.map((r, i) => (
                  <div key={i} style={styles.resultItem}>
                    <div style={styles.resultRuleName}>
                      {r.ruleName}
                      <span style={{
                        ...styles.resultStatus,
                        color: r.passed ? '#276749' : '#c53030'
                      }}>
                        {r.passed ? '✓' : '✗'}
                      </span>
                    </div>
                    {r.errors.map((e, j) => (
                      <div key={j} style={styles.resultError}>{e}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  content: { display: 'grid', gridTemplateColumns: '1fr 400px', gap: '1.5rem' },
  rulesPanel: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  testPanel: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  panelTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  ruleCard: { padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '0.75rem' },
  ruleHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' },
  ruleName: { fontWeight: '500' },
  severityBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  ruleDescription: { margin: '0', fontSize: '0.75rem', color: '#718096' },
  configSection: { marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' },
  configField: { marginBottom: '0.5rem' },
  configLabel: { display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#4a5568', marginBottom: '0.25rem' },
  configInput: { width: '100%', padding: '0.375rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.75rem' },
  saveButton: { width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer', marginTop: '1rem' },
  testInput: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.75rem', fontFamily: 'monospace', resize: 'vertical' },
  testButton: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.875rem', cursor: 'pointer', marginTop: '0.5rem' },
  resultPanel: { marginTop: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' },
  resultHeader: { padding: '0.75rem', fontWeight: '600', textAlign: 'center' },
  resultItem: { padding: '0.5rem 0.75rem', borderTop: '1px solid #e2e8f0' },
  resultRuleName: { display: 'flex', justifyContent: 'space-between', fontWeight: '500', fontSize: '0.875rem' },
  resultStatus: { fontWeight: '600' },
  resultError: { fontSize: '0.75rem', color: '#c53030', marginTop: '0.25rem' }
};
